import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import {
  asapScheduler,
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  EMPTY,
  filter,
  finalize,
  first,
  map,
  merge,
  mergeMap,
  Observable,
  observeOn,
  of,
  share,
  shareReplay,
  skip,
  Subject,
  Subscription,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs';

import Graphic from '@arcgis/core/Graphic';
import AreaMeasurement2DWidget from '@arcgis/core/widgets/AreaMeasurement2D';
import DistanceMeasurement2DWidget from '@arcgis/core/widgets/DistanceMeasurement2D';
import Draw from '@arcgis/core/views/draw/Draw';

import {
  AreaUnit,
  EsriUtils,
  LengthUnit,
} from 'app/shared/utils/esri.utils';
import { PortugalWkidService } from 'app/shared/services/portugal-wkid.service';
import { FormatterUtils } from 'app/shared/utils/formatter.utils';
import {
  measurement,
  generateTextSymbolDefault,
  textSymbolDefault,
} from '../../default-symbols';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import SketchWidget from '@arcgis/core/widgets/Sketch';
import {
  UnitType,
  unitTypeSuffixDictionary,
  unitTypeToSquareDictionary,
} from 'app/shared/constants/unit.constant';
import GroupLayer from '@arcgis/core/layers/GroupLayer';
import { MainMapService } from 'app/shared/components/main-map/main-map.service';
import Polyline from '@arcgis/core/geometry/Polyline';
import Polygon from '@arcgis/core/geometry/Polygon';
import { ScreenService } from 'app/shared/services/screen.service';
import { TranslationService } from '@services/translation.service';

enum MeasurementType {
  Area,
  Distance,
  Coords,
  Radius,
}

interface MeasurementTool {
  type: MeasurementType;
  icon: string;
  text: string;
  label?: string;
  hover?: boolean;
}

interface UnitItemLike {
  text: string;
  value: UnitType;
}

@Component({
  selector: 'app-measurement',
  templateUrl: 'measurement.component.html',
  styleUrls: ['measurement.component.scss'],
})
export class MeasurementComponent implements OnInit, OnChanges, OnDestroy {
  @Input()
  mapView: __esri.MapView;
  @Input()
  measurementGroupLayer: __esri.GroupLayer;

  measurementToolList: MeasurementTool[];
  activeTool: MeasurementTool;

  measurementToolTypes = MeasurementType;

  get isMeasurementToolActive() {
    return !!this.activeTool;
  }

  get activeToolType() {
    return this.activeTool?.type;
  }

  get isUnitSelectDisabled() {
    return !this.isMeasurementToolActive;
  }

  unitList: UnitItemLike[] = [
    {
      text: this.translationService.getCurrentLanguage()['meters'] || 'metros',
      value: UnitType.Meters,
    },
    {
      text: this.translationService.getCurrentLanguage()['kilometers'] || 'quilômetros',
      value: UnitType.Kilometers,
    },
  ];
  unitType: UnitType;

  unitTypes = UnitType;

  private unitSubject = new BehaviorSubject<UnitType>(null);

  private measurementSubscription: Subscription;

  isMobileModeObservable = this.screenService.observeIsMobile();

  private lifecycle = new Subject<void>();

  constructor(
    private mainMapService: MainMapService,
    private portugalWkidService: PortugalWkidService,
    private screenService: ScreenService,
    public translationService: TranslationService
  ) {}

  ngOnInit(): void {
    this.measurementToolList = [
      {
        type: MeasurementType.Area,
        icon: 'assets/icons/draw-polygon.svg',
        text: this.translationService.getCurrentLanguage()['measure'] || 'Área',
      },
      {
        type: MeasurementType.Distance,
        icon: 'assets/icons/line.svg',
        text: this.translationService.getCurrentLanguage()['distance'] || 'Distância',
      },
      {
        type: MeasurementType.Coords,
        icon: 'assets/icons/point.svg',
        text: this.translationService.getCurrentLanguage()['point'] || 'Ponto',
        label: this.translationService.getCurrentLanguage()['coordinates'] || 'Coordenadas',
      },
      {
        type: MeasurementType.Radius,
        icon: 'assets/icons/circle-dashed.svg',
        text: this.translationService.getCurrentLanguage()['radius'] || 'Raio',
      },
    ];

    this.mainMapService.onRequestClear
      .pipe(takeUntil(this.lifecycle))
      .subscribe(() => {
        this.handleActiveTool(null);
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      (changes.mapView && !changes.mapView.firstChange) ||
      (changes.measurementGroupLayer &&
        !changes.measurementGroupLayer.firstChange)
    ) {
      this.destroyMeasurementSession();

      if (this.mapView) {
        this.handleActiveTool(this.activeTool);
      }
    }
  }

  ngOnDestroy(): void {
    this.lifecycle.next();
    this.lifecycle.complete();

    this.destroyMeasurementSession();
  }

  onMeasurementToolClick(e, tool: MeasurementTool) {
    this.handleActiveTool(this.isMeasurementToolItemActive(tool) ? null : tool);
  }

  onBtnClearClick(e) {
    this.handleActiveTool(null);
  }

  onUnitChange(unitType?: UnitType) {
    if (unitType === null || unitType === undefined) {
      unitType = this.unitList?.[0]?.value;
    }

    if (unitType !== this.unitType) {
      this.unitType = unitType;
      this.unitSubject.next(this.unitType);
    }
  }

  isMeasurementToolItemActive(tool: MeasurementTool) {
    return this.activeTool === tool;
  }

  private handleActiveTool(tool: MeasurementTool) {
    if (!tool) {
      this.activeTool = null;
    } else {
      this.activeTool = tool;
    }

    this.unitList = [];

    this.destroyMeasurementSession();

    if (!this.isMeasurementToolActive || !this.mapView) {
      return;
    }

    switch (this.activeToolType) {
      case MeasurementType.Coords:
        this.handlePointWidget();
        break;
      case MeasurementType.Radius:
        this.handleRadiusWidget();
        break;
      case MeasurementType.Distance:
        this.handleDistanceWidget();
        break;
      case MeasurementType.Area:
        this.handleAreaWidget();
        break;
    }

    switch (this.activeToolType) {
      case MeasurementType.Area:
        this.unitList = [
          {
            text: this.translationService.getCurrentLanguage()['square-meters'] || 'Metros quadrados',
            value: UnitType.SqMeters,
          },
          {
            text: this.translationService.getCurrentLanguage()['square-kilometers'] || 'Quilómetros quadrados',
            value: UnitType.SqKilometers,
          },
        ];
        break;
      case MeasurementType.Distance:
      case MeasurementType.Radius:
        this.unitList = [
          {
            text: this.translationService.getCurrentLanguage()['meters'] || 'Metros',
            value: UnitType.Meters,
          },
          {
            text: this.translationService.getCurrentLanguage()['kilometers'] || 'Quilômetros',
            value: UnitType.Kilometers,
          },
        ];
        break;
      case MeasurementType.Coords:
        this.unitList = [
          {
            text: 'Graus',
            value: UnitType.Degrees,
          },
          {
            text: 'DMS',
            value: UnitType.DMS,
          },
        ];
        break;
    }

    this.onUnitChange();
  }

  private destroyMeasurementSession() {
    this.measurementSubscription?.unsubscribe();
  }

  private handlePointWidget() {
    this.destroyMeasurementSession();

    const mapView = this.mapView;

    const groupLayer: __esri.LayersMixin =
      this.measurementGroupLayer ?? mapView.map;

    const groupLayerAux = new GroupLayer();
    groupLayer.add(groupLayerAux);

    const subscription = new Subscription();

    this.mainMapService.disableMapPopup();
    subscription.add(() => {
      this.mainMapService.enableMapPopup();
    });

    const pointObservable = EsriUtils.leftClicks(mapView).pipe(
      switchMap((e) => {
        return this.portugalWkidService.projectGeomToAreaWkid(
          e.mapPoint as __esri.Point
        );
      }),
      share()
    );

    subscription.add(
      pointObservable
        .pipe(
          mergeMap((point) => {
            if (!point) {
              return EMPTY;
            }

            const graphicsLayer = new GraphicsLayer();
            groupLayerAux.add(graphicsLayer);

            graphicsLayer.add(
              new Graphic({
                geometry: point,
                symbol: measurement.pointSymbolDefault,
              })
            );

            let textGraphic: Graphic;
            return merge(
              this.unitSubject.pipe(take(1)),
              this.unitSubject.pipe(skip(1), observeOn(asapScheduler))
            ).pipe(
              finalize(() => {
                groupLayerAux.remove(graphicsLayer);
                graphicsLayer.destroy();
              }),
              tap((unitType) => {
                let xFormatted: string;
                let yFormatted: string;

                let boxTextWidth: number;

                switch (unitType) {
                  case UnitType.DMS:
                    xFormatted = FormatterUtils.formatDMS(point.x, 'long');
                    yFormatted = FormatterUtils.formatDMS(point.y, 'lat');
                    boxTextWidth = 140;
                    break;
                  case UnitType.Degrees:
                  default:
                    xFormatted = FormatterUtils.formatNumber(point.x);
                    yFormatted = FormatterUtils.formatNumber(point.y);
                    boxTextWidth = 100;
                    break;
                }

                if (textGraphic) {
                  graphicsLayer.remove(textGraphic);
                }

                const coordsFormatted = [
                  `X: ${xFormatted}`,
                  `Y: ${yFormatted}`,
                ].join('\n');

                textGraphic = new Graphic({
                  geometry: point,
                  symbol: generateTextSymbolDefault(coordsFormatted, {
                    boxWidth: boxTextWidth,
                    boxHeight: 30,
                    offsetY:
                      measurement.pointSymbolDefault.size / 2 +
                      (measurement.pointSymbolDefault.outline?.width ?? 0) +
                      3,
                  }),
                });

                graphicsLayer.add(textGraphic);
              })
            );
          })
        )
        .subscribe()
    );

    subscription.add(() => {
      groupLayer.remove(groupLayerAux);
      groupLayerAux.destroy();
    });

    this.measurementSubscription = subscription;
  }

  private handleRadiusWidget() {
    this.destroyMeasurementSession();

    const mapView = this.mapView;

    const groupLayer: __esri.LayersMixin =
      this.measurementGroupLayer ?? mapView.map;

    const drawingLayer = new GraphicsLayer();
    groupLayer.add(drawingLayer);

    const graphicsGroupLayer = new GroupLayer();
    groupLayer.add(graphicsGroupLayer);

    const subscription = new Subscription();

    this.mainMapService.disableMapPopup();
    subscription.add(() => {
      this.mainMapService.enableMapPopup();
    });

    const sketch = new SketchWidget({
      view: mapView,
      layer: drawingLayer,
      creationMode: 'single',
    });

    sketch.viewModel.polygonSymbol = measurement.polygonSymbolDefault;

    const sketchCreateObservable = EsriUtils.on(sketch, 'create').pipe(share());
    const sketchStartObservable = sketchCreateObservable.pipe(
      first((evt: __esri.SketchCreateEvent) => evt?.state === 'start')
    );
    const sketchCompleteObservable = sketchCreateObservable.pipe(
      first((evt: __esri.SketchCreateEvent) => evt?.state === 'complete'),
      share()
    );
    const sketchActiveObservable = sketchCreateObservable.pipe(
      filter((evt: __esri.SketchCreateEvent) => evt?.state === 'active'),
      takeUntil(sketchCompleteObservable)
    );
    const sketchCancelObservable = sketchCreateObservable.pipe(
      first((evt: __esri.SketchCreateEvent) => evt?.state === 'cancel'),
      takeUntil(sketchCompleteObservable)
    );

    let sketchGraphicSubscription: Subscription;
    subscription.add(() => {
      sketchGraphicSubscription?.unsubscribe();
    });

    const sketchGraphicObservableFn = () => {
      sketchGraphicSubscription?.unsubscribe();
      sketchGraphicSubscription = sketchStartObservable
        .pipe(
          mergeMap(() => {
            return new Observable<__esri.SketchCreateEvent>((subscriber) => {
              const graphicsLayer = new GraphicsLayer();
              graphicsGroupLayer.add(graphicsLayer);

              let subscriptionInternal: Subscription;

              const cancelFn = () => {
                subscriptionInternal?.unsubscribe();
                subscriber.complete();

                drawingLayer.removeAll();
                graphicsGroupLayer.remove(graphicsLayer);
                graphicsLayer.destroy();

                sketchGraphicObservableFn();
              };

              subscriber.add(sketchCancelObservable.subscribe(cancelFn));

              const cancelOnCompleteSubscription =
                sketchCompleteObservable.subscribe(cancelFn);

              subscriber.add(cancelOnCompleteSubscription);

              subscriber.add(
                sketchActiveObservable
                  .pipe(
                    first(),
                    mergeMap((evt) => {
                      cancelOnCompleteSubscription.unsubscribe();

                      return merge(
                        of(evt),
                        sketchActiveObservable,
                        sketchCompleteObservable
                      );
                    }),
                    map((evt) => evt?.graphic)
                  )
                  .subscribe({
                    next: (graphic) => {
                      subscriptionInternal?.unsubscribe();
                      subscriptionInternal = merge(
                        this.unitSubject.pipe(take(1)),
                        this.unitSubject.pipe(skip(1), observeOn(asapScheduler))
                      ).subscribe((unitType) => {
                        graphicsLayer.removeAll();

                        let areaUnitStr: AreaUnit;
                        switch (unitType) {
                          case UnitType.Meters:
                            areaUnitStr = 'square-meters';
                            break;
                          case UnitType.Kilometers:
                            areaUnitStr = 'square-kilometers';
                            break;
                        }

                        const polygon = graphic.geometry as __esri.Polygon;
                        const area = EsriUtils.calculateArea(
                          polygon,
                          areaUnitStr
                        );
                        const radius = EsriUtils.calculateRadiusByArea(area);

                        const radiusFormatted = `${FormatterUtils.formatNumber(
                          radius,
                          3
                        )} ${unitTypeSuffixDictionary.get(unitType)}`;
                        const areaFormatted = `${FormatterUtils.formatNumber(
                          area,
                          3
                        )} ${unitTypeSuffixDictionary.get(
                          unitTypeToSquareDictionary.get(unitType)
                        )}`;

                        graphicsLayer.add(graphic);

                        const line = EsriUtils.getRadiusLine(
                          polygon,
                          'horizontal'
                        );
                        graphicsLayer.add(
                          new Graphic({
                            geometry: line,
                            symbol: measurement.polygonSymbolDefault,
                          })
                        );

                        const areaTextSymbol = textSymbolDefault.clone();
                        areaTextSymbol.yoffset = 5;
                        areaTextSymbol.text = `Área: ${areaFormatted}`;
                        graphicsLayer.add(
                          new Graphic({
                            geometry: EsriUtils.rotate(
                              polygon.getPoint(0, 0),
                              90,
                              polygon.centroid
                            ),
                            symbol: areaTextSymbol,
                          })
                        );

                        const radiusTextSymbol = textSymbolDefault.clone();
                        radiusTextSymbol.yoffset = 5;
                        radiusTextSymbol.text = `Raio: ${radiusFormatted}`;
                        graphicsLayer.add(
                          new Graphic({
                            geometry: EsriUtils.getCentroidFromLine(line),
                            symbol: radiusTextSymbol,
                          })
                        );
                      });
                    },
                    complete: () => {
                      if (
                        subscriptionInternal &&
                        !subscriptionInternal.closed
                      ) {
                        subscription.add(subscriptionInternal);
                      }

                      sketchGraphicObservableFn();
                    },
                  })
              );
            });
          })
        )
        .subscribe();

      sketch.cancel();
      sketch.create('circle');
    };

    sketchGraphicObservableFn();

    subscription.add(() => {
      sketch.cancel();
      sketch.destroy();

      groupLayer.remove(drawingLayer);
      drawingLayer.destroy();
      groupLayer.remove(graphicsGroupLayer);
      graphicsGroupLayer.destroy();
    });

    this.measurementSubscription = subscription;
  }

  private handleDistanceWidget() {
    this.restartMeasurementWork({
      createAction: (draw) => {
        return draw.create('polyline', { mode: 'click' });
      },
      createGeom: (evt, action, mapView) => {
        return new Polyline({
          paths: [evt.vertices],
          spatialReference: mapView.spatialReference,
        });
      },
      doDrawing: (graphicsLayer, geometry, unitType) => {
        graphicsLayer.removeAll();

        graphicsLayer.add(
          new Graphic({
            geometry,
            symbol: measurement.polylineSymbolDefault,
          })
        );

        let lengthUnitStr: LengthUnit;
        switch (unitType) {
          case UnitType.Meters:
            lengthUnitStr = 'meters';
            break;
          case UnitType.Kilometers:
            lengthUnitStr = 'kilometers';
            break;
        }

        const distance = EsriUtils.calculateLength(geometry, lengthUnitStr);
        if (distance <= 0) {
          return;
        }

        const distanceFormatted = `${FormatterUtils.formatNumber(
          distance,
          3
        )} ${unitTypeSuffixDictionary.get(unitType)}`;

        const point = EsriUtils.getCentroidFromLine(geometry);

        const areaTextSymbol = textSymbolDefault.clone();
        areaTextSymbol.yoffset = 5;
        areaTextSymbol.text = distanceFormatted;

        graphicsLayer.add(
          new Graphic({
            geometry: point,
            symbol: areaTextSymbol,
          })
        );
      },
    });
  }

  private handleAreaWidget() {
    this.restartMeasurementWork({
      createAction: (draw) => {
        return draw.create('polygon', { mode: 'click' });
      },
      createGeom: (evt, action, mapView) => {
        return new Polygon({
          rings: [evt.vertices],
          spatialReference: mapView.spatialReference,
        });
      },
      doDrawing: (graphicsLayer, geometry, unitType) => {
        graphicsLayer.removeAll();

        graphicsLayer.add(
          new Graphic({
            geometry,
            symbol: measurement.polygonSymbolDefault,
          })
        );

        let areaUnitStr: AreaUnit;
        switch (unitType) {
          case UnitType.SqMeters:
            areaUnitStr = 'square-meters';
            break;
          case UnitType.SqKilometers:
            areaUnitStr = 'square-kilometers';
            break;
        }

        const area = EsriUtils.calculateArea(geometry, areaUnitStr);
        if (area <= 0) {
          return;
        }

        const areaFormatted = `${FormatterUtils.formatNumber(
          area,
          3
        )} ${unitTypeSuffixDictionary.get(unitType)}`;

        const point = geometry.centroid;

        const areaTextSymbol = textSymbolDefault.clone();
        areaTextSymbol.yoffset = 5;
        areaTextSymbol.text = areaFormatted;

        graphicsLayer.add(
          new Graphic({
            geometry: point,
            symbol: areaTextSymbol,
          })
        );
      },
    });
  }

  private restartMeasurementWork<G extends __esri.Geometry>(cfg: {
    createAction: (draw: __esri.Draw) => __esri.DrawAction;
    createGeom: (
      evt:
        | __esri.SegmentDrawActionVertexAddEvent
        | __esri.SegmentDrawActionCursorUpdateEvent
        | __esri.SegmentDrawActionDrawCompleteEvent,
      action: __esri.DrawAction,
      mapView: __esri.MapView
    ) => G;
    doDrawing: (
      graphicsLayer: __esri.GraphicsLayer,
      geometry: G,
      unitType: UnitType
    ) => void;
  }) {
    this.destroyMeasurementSession();

    const mapView = this.mapView;

    const groupLayer: __esri.LayersMixin =
      this.measurementGroupLayer ?? mapView.map;

    const graphicsGroupLayer = new GroupLayer();
    groupLayer.add(graphicsGroupLayer);

    const subscription = new Subscription();

    this.mainMapService.disableMapPopup();
    subscription.add(() => {
      this.mainMapService.enableMapPopup();
    });

    const draw = new Draw({
      view: this.mapView,
    });

    let drawGraphicSubscription: Subscription;
    subscription.add(() => {
      drawGraphicSubscription?.unsubscribe();
    });

    const startDrawGraphicObservableFn = () => {
      drawGraphicSubscription?.unsubscribe();

      if (subscription.closed) {
        return;
      }

      drawGraphicSubscription = new Observable((subscriber) => {
        const graphicsLayer = new GraphicsLayer();
        graphicsGroupLayer.add(graphicsLayer);

        const action = cfg.createAction(draw);
        subscriber.add(() => {
          action.destroy();
        });

        const cancelFn = () => {
          subscriber.complete();

          graphicsGroupLayer.remove(graphicsLayer);
          graphicsLayer.destroy();

          startDrawGraphicObservableFn();
        };

        const cancelObservable = EsriUtils.on(mapView, 'key-down').pipe(
          first((e) => {
            return e.key === 'Escape';
          })
        );
        subscriber.add(cancelObservable.subscribe(cancelFn));

        const actionCompleteObservable =
          EsriUtils.on<__esri.SegmentDrawActionDrawCompleteEvent>(
            action,
            'draw-complete'
          ).pipe(share());

        const cancelOnCompleteSubscription =
          actionCompleteObservable.subscribe(cancelFn);

        subscriber.add(cancelOnCompleteSubscription);

        const vertexAddedObservable =
          EsriUtils.on<__esri.SegmentDrawActionVertexAddEvent>(
            action,
            'vertex-add'
          );

        const geometryObservable = vertexAddedObservable.pipe(
          first(),
          mergeMap((evt) => {
            cancelOnCompleteSubscription?.unsubscribe();

            return merge(
              EsriUtils.on<__esri.SegmentDrawActionCursorUpdateEvent>(
                action,
                'cursor-update'
              ),
              actionCompleteObservable
            );
          }),
          map((evt) => {
            return cfg.createGeom(evt, action, mapView);
          }),
          takeUntil(actionCompleteObservable),
          shareReplay({
            bufferSize: 1,
            refCount: true,
          })
        );

        const unitTypeObservable = merge(
          this.unitSubject.pipe(take(1)),
          this.unitSubject.pipe(skip(1), observeOn(asapScheduler))
        ).pipe(
          shareReplay({
            bufferSize: 1,
            refCount: true,
          }),
          distinctUntilChanged()
        );

        const observableFnAux = (geometryObservable: Observable<G>) => {
          return combineLatest([geometryObservable, unitTypeObservable]).pipe(
            tap(([geometry, unitType]) => {
              cfg.doDrawing(graphicsLayer, geometry, unitType);
            })
          );
        };

        const subscriptionAux = observableFnAux(
          geometryObservable.pipe(
            tap({
              complete: () => {
                subscriptionAux.unsubscribe();

                if (!subscription.closed) {
                  subscription.add(
                    observableFnAux(geometryObservable).subscribe()
                  );

                  startDrawGraphicObservableFn();
                }
              },
            })
          )
        ).subscribe();

        subscriber.add(subscriptionAux);
      }).subscribe();
    };

    startDrawGraphicObservableFn();

    subscription.add(() => {
      draw.reset();
      draw.destroy();

      groupLayer.remove(graphicsGroupLayer);
      graphicsGroupLayer.destroy();
    });

    this.measurementSubscription = subscription;
  }
}
